import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, CircularProgress, TextField, Typography } from '@mui/material';
import { PillButton, sa } from '../../superAdminUi';
import { changeUserEmail } from '../../../services/api/identityUserService';
import { fieldSx, hintSx, labelSx, noteSx, tone } from './panelStyles';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// The sign-in email, changed in place. A change is never applied directly: a confirmation link
// goes to the new address and the email switches only once that address confirms (48h). It is
// its own action — separate from Save — because nothing changes until the user confirms.
function EmailField({ userId, email, disabled }) {
  const [mode, setMode] = useState('view'); // view | editing | sending | sent
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [ssoWarning, setSsoWarning] = useState('');

  const cancel = () => {
    setMode('view');
    setValue('');
    setError('');
  };

  const send = async () => {
    const next = value.trim();
    if (!EMAIL_PATTERN.test(next)) {
      setError('Enter a valid email address.');
      return;
    }
    if (next.toLowerCase() === email.toLowerCase()) {
      setError('That’s already this user’s email address.');
      return;
    }
    setMode('sending');
    setError('');
    try {
      const result = await changeUserEmail(userId, next);
      setSentTo(result?.sentTo || next);
      setSsoWarning(result?.ssoWarning || '');
      setMode('sent');
    } catch (err) {
      setError(
        err?.status === 409
          ? 'That email address is already used by another account.'
          : err?.message || 'The change couldn’t be requested. Try again.'
      );
      setMode('editing');
    }
  };

  return (
    <Box>
      <Typography component='label' htmlFor='edit-user-email' sx={labelSx}>Email</Typography>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField id='edit-user-email' value={email} fullWidth size='small' disabled sx={fieldSx} />
        {mode === 'view' && (
          <PillButton type='button' $variant='outline' onClick={() => setMode('editing')} disabled={disabled}>
            Change
          </PillButton>
        )}
      </Box>

      {(mode === 'editing' || mode === 'sending') && (
        <Box sx={{ mt: 1.25, p: 1.5, border: `1px solid ${sa.color.stroke}`, borderRadius: sa.radius.md }}>
          <Typography component='label' htmlFor='edit-user-new-email' sx={labelSx}>New email address</Typography>
          <TextField
            id='edit-user-new-email'
            type='email'
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setError('');
            }}
            placeholder='name@school.org.uk'
            fullWidth
            size='small'
            autoFocus
            error={Boolean(error)}
            helperText={error || 'We’ll email a confirmation link to this address. The change only happens once they confirm.'}
            disabled={mode === 'sending'}
            sx={fieldSx}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.25 }}>
            <PillButton type='button' $variant='outline' onClick={cancel} disabled={mode === 'sending'}>
              Cancel
            </PillButton>
            <PillButton type='button' $variant='teal' onClick={send} disabled={mode === 'sending'}>
              {mode === 'sending' ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Send confirmation link'}
            </PillButton>
          </Box>
        </Box>
      )}

      {mode === 'sent' && (
        <Box sx={{ ...noteSx(tone.added), mt: 1.25 }} role='status'>
          Confirmation link sent to <strong>{sentTo}</strong>. The email changes once they confirm it (the link lasts 48
          hours).
          {ssoWarning && <Box sx={{ mt: 0.5 }}>{ssoWarning}</Box>}
        </Box>
      )}

      {mode === 'view' && (
        <Typography sx={{ ...hintSx, mt: 0.5 }}>Changing the email sends a confirmation link to the new address.</Typography>
      )}
    </Box>
  );
}

EmailField.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  email: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
};

export default EmailField;
